import { useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";

/**
 * A settled image that drew nothing: no width and no height. Never an SVG, because a browser may
 * report a natural size of 0 for an SVG that sets only a `viewBox` (Firefox does), and ga4, sftp,
 * mouseflow and both destination logos are written that way. A broken SVG still fires `error`.
 */
function drewNothing(image: HTMLImageElement, src: string): boolean {
  if (/\.svg(?:[?#]|$)/i.test(src)) return false;
  return image.naturalWidth === 0 && image.naturalHeight === 0;
}

/**
 * A connector's logo at 28 px (Session 11, P1; owner feedback item 6).
 *
 * `alt=""` and `aria-hidden`, because the connector's name is always right beside it: a logo
 * captioned by its own product name is decoration, and naming it would have a screen reader say
 * "SQL Server SQL Server". `SkyAvatar` is not used: it crops to a circle and names itself.
 *
 * The `iconUrl` is a SITE-RELATIVE path (`/connector-logos/<name>.svg`) served from this app's own
 * static bundle, so it is written verbatim as the image source. With no `iconUrl` (a connector
 * that ships without one), or when the image fails to load (a definition uploaded through the
 * admin API pointing at a vendor URL that has since moved), it renders a 28 px muted square with
 * the connector's first letter instead: a broken-image glyph is worse than no image, and an empty
 * gap would leave the column ragged.
 *
 * Dark mode (B-70): the vendors' files are drawn for a light page and some (Mouseflow, Ninety,
 * Paycom) are near-black, so the image sits on a light tile there (`dark:bg-logo-tile`) rather than
 * being recoloured. Light mode draws the bare image as before.
 *
 * Issue #1146: the image is not `loading="lazy"` (28 px files, a handful per screen: a deferred
 * load that never starts leaves a blank gap with no error to fall back on), and a raster load that ends
 * with no size falls back to the letter like a load error does (an SVG never does, see `drewNothing`).
 * The fallback logs which path fired (`skydata.logo.reason`, the site-relative path only).
 */
export function ConnectorLogo({
  iconUrl,
  name,
  className,
}: {
  iconUrl: string | null | undefined;
  /** The connector's display name; its first letter is the monogram. */
  name: string;
  className?: string;
}) {
  // Keyed on the source, so a different logo gets a fresh attempt.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const extra = className ? ` ${className}` : "";
  const imageRef = useRef<HTMLImageElement>(null);
  // An image that finished (or failed) before React attached `onError` never fires it again, and
  // one that "loads" with no pixels (an empty or blocked body) is not an error event either.
  // Both would leave an empty 28 px gap (issue #1146), so a settled image with no width is a
  // failure here too.
  useEffect(() => {
    const image = imageRef.current;
    if (image && iconUrl && image.complete && drewNothing(image, iconUrl)) {
      logger.warn("connector logo drew nothing", {
        "skydata.logo.path": iconUrl,
        "skydata.logo.reason": "settled-empty",
      });
      setFailedSrc(iconUrl);
    }
  }, [iconUrl]);
  if (!iconUrl || failedSrc === iconUrl) {
    const letter = name.trim().charAt(0).toUpperCase() || "?";
    return (
      <span
        aria-hidden="true"
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-sm font-semibold text-muted-foreground${extra}`}
        data-testid="connector-logo-monogram"
      >
        {letter}
      </span>
    );
  }
  return (
    <img
      ref={imageRef}
      src={iconUrl}
      alt=""
      aria-hidden="true"
      width={28}
      height={28}
      className={`h-7 w-7 shrink-0 rounded-md object-contain dark:bg-logo-tile dark:p-0.5${extra}`}
      onError={() => {
        logger.warn("connector logo failed to load", {
          "skydata.logo.path": iconUrl,
          "skydata.logo.reason": "error",
        });
        setFailedSrc(iconUrl);
      }}
      onLoad={(event) => {
        if (!drewNothing(event.currentTarget, iconUrl)) return;
        logger.warn("connector logo drew nothing", {
          "skydata.logo.path": iconUrl,
          "skydata.logo.reason": "load-empty",
        });
        setFailedSrc(iconUrl);
      }}
      data-testid="connector-logo"
    />
  );
}
